import * as ko from "knockout";
import * as _ from "underscore";
import * as moment from "moment";
import * as localisationHelper from "localisationHelper";

// Types
import Moment = moment.Moment;

// Helpers
import "knockoutCustomBindings";

// Models
import { DateTimeConfiguration } from "Content/ts/Models/Shared/DateTime/dateTimePickerConfiguration";

// Services
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";
import { ConfigureControlService } from "Content/ts/Shared/Inputs/DateOfBirth/Services/configureControlService";

// Components
import "Content/ts/shared/Controls/DateTime/datetime-picker";

export const Deps = {
    LocalisationHelper: localisationHelper
}

export interface IDateBirthPicker {
    Value: KnockoutObservable<Moment>;                      // Required

    MinimumAgeDate: Moment | KnockoutObservable<Moment>;    // Optional
    MaximumAgeDate: Moment | KnockoutObservable<Moment>;    // Optional
    StartDate: Moment | KnockoutObservable<Moment>;         // Optional
    ValidationFields: KnockoutObservable<any>[];            // Optional
    IsRequired: boolean | KnockoutComputed<boolean>;        // Optional
    IsReadonly: boolean | KnockoutComputed<boolean>;        // Optional
    DisplayCondition: boolean | KnockoutComputed<boolean>;  // Optional
    ErrorMessage: string;                                   // Optional
}

export default class DateBirthPicker {
    CustomMessages: {
        DateOfBirthValidation: string;
    };

    MinimumAgeDate: Moment | KnockoutObservable<Moment>;
    MaximumAgeDate: Moment | KnockoutObservable<Moment>;
    StartDate: Moment | KnockoutObservable<Moment>;
    DatePickerConfiguration: DateTimeConfiguration;

    IsRequired: boolean | KnockoutComputed<boolean>;
    IsReadonly: boolean | KnockoutComputed<boolean>;
    DisplayCondition: boolean | KnockoutComputed<boolean>;
    ErrorMessage: string;

    Value: KnockoutObservable<Moment>;
    ValidationFields: KnockoutObservable<any>[];

    constructor(params: IDateBirthPicker) {
        // Component Validation
        if (!params.Value) {
            throw new Error("Please ensure all required properties have been passed to 'dob-datepicker' component");
        }

        // Critical Initialisation
        this.RetrieveCustomMessages();

        // Provided Component Properties
        this.Value = params.Value;
        this.IsRequired = params.IsRequired || false;
        this.IsReadonly = params.IsReadonly || false;
        this.DisplayCondition = params.DisplayCondition != null ? params.DisplayCondition : true;
        this.ValidationFields = params.ValidationFields || [];
        this.MinimumAgeDate = params.MinimumAgeDate || moment().startOf("day");     // Purpose: Shouldn't allow the user to select a date which is in the future
        this.MaximumAgeDate = params.MaximumAgeDate || moment("1900-01-01T00:00:00").startOf("day");
        this.StartDate = params.StartDate || ko.observable(moment().add(-30, 'y'));
        this.ErrorMessage = params.ErrorMessage;

        // Standard Component Properties
        this.DatePickerConfiguration = ConfigureControlService.ConfigureDatePickerConfiguration(this, params);

        // Initialisation Methods
        this.SetupValidation();
    }

    private RetrieveCustomMessages(): void {
        this.CustomMessages = {
            DateOfBirthValidation: Deps.LocalisationHelper.getString("JoinResources", "DOB_OutsideValidRange")
        };
    };

    private SetupValidation(): void {
        ValidationService.AddValidationRule(this.Value, this.ValidationFields, {
            validation: [{
                validator: (value?: Moment) => {
                    const minimumAge = ko.unwrap(this.MinimumAgeDate),
                        maximumAge = ko.unwrap(this.MaximumAgeDate);
                    
                    if (!ko.unwrap(this.IsRequired) && !value) {
                        return true;
                    }

                    return !ko.unwrap(this.DisplayCondition) || (value && value.isBetween(maximumAge, minimumAge, null, "[]"));
                },
                message: (): string => {
                    if (this.ErrorMessage) {
                        return this.ErrorMessage;
                    }

                    return this.CustomMessages.DateOfBirthValidation.replace("{0}", Deps.LocalisationHelper.formatDate(ko.unwrap(this.MinimumAgeDate)))
                        .replace("{1}", Deps.LocalisationHelper.formatDate(ko.unwrap(this.MaximumAgeDate)));
                }
            }]
        });
    };

    private removeValidation() {
        ValidationService.RemoveValidationRule(this.Value, this.ValidationFields);
    };

    public dispose() {
        this.removeValidation();
    };
};

const componentName = "dob-datepicker";
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: DateBirthPicker,
    template: { require: `text!Content/ts/Shared/Inputs/DateOfBirth/${componentName}.html` }
});